import applicationReducer from '_store/application/reducer';
import { dialogSelector, selectionSelector } from '_store/application/selectors';
import { DialogType } from '_store/application/types';
import { spatemByIdSelector } from '_store/spatem/selectors';
import { SpatemActivityStatus } from '_store/spatem/spatemsTypes';
import { getSpatemActivityStatus } from '_store/spatem/utils';
import L, { Map } from 'leaflet';
import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Marker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';

import { canInfoViewBeOpened } from '../../../InfoView/utils';
import { encodeClusterMeta, spatemClusterStatus, useClusterRefresh } from '../../clustering';
import useMarkerPosition from '../../hooks/useMarkerPosition';
import SpatemInfoTooltip from '../../InfoTooltips/SpatemInfoTooltip';
import { HoveringElementType } from '../../InfoTooltips/types';
import { iconSizePx, spatemIcon } from '../../utils';

const iconSize = new L.Point(iconSizePx, iconSizePx);
const iconAnchor = new L.Point(iconSize.x / 2, iconSize.y);
const tooltipOffset = new L.Point(0, -iconSize.y);

const SpatemMarker: FC<{
  id: string;
  mapRef: Map;
}> = ({ id, mapRef }): ReactElement => {
  const dispatch = useDispatch();
  const markerRef = useRef<L.Marker>(null);
  const refreshClusters = useClusterRefresh();

  // Get spatem data from store using selector - this will update when data changes
  const spatemSelector = useMemo(() => spatemByIdSelector(id), [id]);
  const spatem = useSelector(spatemSelector);

  const dialog = useSelector(dialogSelector);
  const selection = useSelector(selectionSelector);
  const canOpenInfo = canInfoViewBeOpened(dialog, selection);

  // Force re-render every second to update activity status based on time
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Early return if spatem not found
  if (!spatem) {
    return null;
  }

  // Calculate activity status - only depends on updated_at, not the whole spatem object
  const activityStatus = useMemo(
    () => getSpatemActivityStatus(spatem),
    [spatem.updated_at], // Only recalculate when updated_at changes
  );

  const markerPosition = useMarkerPosition(spatem?.location?.coordinates);

  const isSpatemSelected =
    dialog &&
    [DialogType.SPATEM_LIST, DialogType.SINGLE_SPATEM].includes(dialog.type) &&
    dialog.id === id;

  // Memoize icon based only on activity status and selection state
  const markerIcon = useMemo(
    () =>
      new L.Icon({
        iconSize,
        iconUrl: spatemIcon(activityStatus),
        iconAnchor,
        className: `marker-event-${isSpatemSelected ? 'selected' : 'unselected'}`,
      }),
    [activityStatus, isSpatemSelected], // Only recreate icon when status or selection changes
  );

  const clusterAttribution = encodeClusterMeta({
    group: 'spatem',
    status: spatemClusterStatus(activityStatus === SpatemActivityStatus.ACTIVE),
  });

  // Update icon / cluster meta when activity status or selection changes without recreating marker
  const skipFirstRefresh = useRef(true);
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setIcon(markerIcon);
      markerRef.current.options.attribution = clusterAttribution;
    }
    if (skipFirstRefresh.current) {
      skipFirstRefresh.current = false;
      return;
    }
    refreshClusters();
  }, [markerIcon, clusterAttribution, refreshClusters]);

  const eventHandlers = useMemo(
    () => ({
      click: () => {
        if (canOpenInfo) {
          dispatch(
            applicationReducer.actions.infoSelected({
              type: DialogType.SINGLE_SPATEM,
              id,
            }),
          );
          if (markerPosition) {
            mapRef.panTo(markerPosition);
          }
        }
      },
    }),
    [mapRef, id, markerPosition, dispatch, canOpenInfo],
  );

  if (!markerPosition) {
    return null;
  }

  return (
    <div>
      <Marker
        ref={markerRef}
        position={markerPosition}
        icon={markerIcon}
        interactive={true}
        eventHandlers={eventHandlers}
        attribution={clusterAttribution}
      >
        <SpatemInfoTooltip
          hoveringElement={{
            type: HoveringElementType.SPATEM,
            spatem,
            offset: tooltipOffset,
          }}
        />
      </Marker>
    </div>
  );
};

// Memoize - props are now stable (only id and mapRef), so component only re-renders
// when selector returns new data, but marker won't be recreated due to stable key
export default React.memo(SpatemMarker);

